import React from 'react';
import { Box, Button, Icon, Text } from '@chakra-ui/react';
import { LuRepeat } from 'react-icons/lu';
import { Tooltip } from '@/components/ui/tooltip.tsx';
import {
	COMPOSER_BAR_CONTROL_H,
	COMPOSER_REPEAT_FULL_LABEL_MIN_WIDTH,
	COMPOSER_REPEAT_SHORT_LABEL_MIN_WIDTH,
	COMPOSER_REPEAT_WHEN_COMPLETED_LABEL,
	COMPOSER_TOOLTIP_CLOSE_DELAY_MS,
	COMPOSER_TOOLTIP_OPEN_DELAY_MS,
	composerRowAtLeast as rowAtLeast,
} from './composerConstants.ts';

// Full sentence -> Repeat -> icon and track, with padding drawn in for the smallest band.
const REPEAT_PILL_CSS = {
	paddingInline: '8px',
	gap: '4px',
	[rowAtLeast(COMPOSER_REPEAT_SHORT_LABEL_MIN_WIDTH)]: { paddingInline: '12px', gap: '8px' },
} as const;
const REPEAT_SHORT_LABEL_CSS = {
	display: 'none',
	[rowAtLeast(COMPOSER_REPEAT_SHORT_LABEL_MIN_WIDTH)]: { display: 'inline' },
	[rowAtLeast(COMPOSER_REPEAT_FULL_LABEL_MIN_WIDTH)]: { display: 'none' },
} as const;
const REPEAT_FULL_LABEL_CSS = {
	display: 'none',
	[rowAtLeast(COMPOSER_REPEAT_FULL_LABEL_MIN_WIDTH)]: { display: 'inline' },
} as const;

interface ComposerRepeatToggleProps {
	isRecurring: boolean;
	onToggle: () => void;
	disabled?: boolean;
}

/**
 * The Task tab's recurrence control on the composer bar. Styled as a labelled pill (not a bare
 * Switch) because it sits inline in the action row beside the submit button, where a standalone
 * switch would read as detached chrome; the embedded track keeps the on/off state legible while
 * the label explains what it toggles.
 */
const ComposerRepeatToggle: React.FC<ComposerRepeatToggleProps> = (props) => {
	const { isRecurring, onToggle, disabled } = props;

	return (
		<Tooltip
			openDelay={COMPOSER_TOOLTIP_OPEN_DELAY_MS}
			closeDelay={COMPOSER_TOOLTIP_CLOSE_DELAY_MS}
			content={COMPOSER_REPEAT_WHEN_COMPLETED_LABEL}
			positioning={{ placement: 'top' }}
		>
			<Button
				type="button"
				onClick={onToggle}
				disabled={disabled}
				aria-label={COMPOSER_REPEAT_WHEN_COMPLETED_LABEL}
				aria-pressed={isRecurring}
				variant="ghost"
				size="sm"
				h={COMPOSER_BAR_CONTROL_H}
				rounded="full"
				px={3}
				gap={2}
				flexShrink={0}
				textStyle="body.sm"
				fontWeight={isRecurring ? 'medium' : 'normal'}
				color={isRecurring ? 'brand.fg' : 'fg.muted'}
				_hover={{ bg: 'surface.subtle' }}
				css={REPEAT_PILL_CSS}
			>
				<Icon as={LuRepeat} boxSize={4} />
				<Text as="span" css={REPEAT_SHORT_LABEL_CSS}>
					Repeat
				</Text>
				<Text as="span" css={REPEAT_FULL_LABEL_CSS}>
					{COMPOSER_REPEAT_WHEN_COMPLETED_LABEL}
				</Text>
				<Box
					as="span"
					position="relative"
					w={8}
					h={4.5}
					borderRadius="full"
					flexShrink={0}
					bg={isRecurring ? 'brand.solid' : 'brand.emphasized'}
					transition="background-color 0.2s ease"
				>
					<Box
						as="span"
						position="absolute"
						top="2px"
						left="2px"
						w={3.5}
						h={3.5}
						borderRadius="full"
						bg="white"
						boxShadow="sm"
						transform={isRecurring ? 'translateX(14px)' : 'translateX(0)'}
						transition="transform 0.2s ease"
					/>
				</Box>
			</Button>
		</Tooltip>
	);
};

export default ComposerRepeatToggle;
